import _ from "lodash";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";

import { getRequestMessage } from "../../TransactionDetailsInfo/utils/getRequestStatus";

import * as Progress from "~/components/Progress";
import Spacer, { Axis } from "~/components/Spacer";
import { Text } from "~/components/Text";
import { TransactionStatus } from "~/endpoints/transactions/types";
import { Direction, TransactionWrap } from "~/hooks/use-transaction";
import useCurrentViewerVaspDID from "~/hooks/useCurrentViewerVaspDID";
import { getProgress, getStatusText } from "~/templates/transactions/TransactionsPage/utils";
import {
  ActionData,
  NOT_READY_TRANSACTION_OPTIONS_BENEFICIARY_MAPPING,
  TransactionActionParsed,
} from "~/templates/transactions/types";
import { formatterDateTime } from "~/utils/formatterDateTime";

const TextStatus = styled(Text)`
  font-size: 12px;
  font-weight: 700;
`;

const DescriptionStatus = styled.p`
  font-size: 10px;
  margin-top: -4px;
  font-weight: 400;
  color: #363636;
`;

const TooltipText = styled.p`
  font-family: Montserrat;
  font-size: 12px;
  font-weight: 400;
  line-height: 14.63px;
  text-align: left;
  margin-bottom: 4px;
`;

const TooltipDateText = styled.p<{ color: string }>`
  font-family: Montserrat;
  font-size: 12px;
  font-weight: 600;
  line-height: 14.63px;
  text-align: left;
  color: ${(props) => props.color};
`;

const TooltipContentContainer = styled.div`
  max-width: 215px;
  font-size: 12px;
  padding: 4px 8px;
`;

const ProgressBarStatus = styled(Progress.Bar)`
  margin-top: 6px;
  margin-bottom: 4px;
  height: 6px !important;
`;
ProgressBarStatus.defaultProps = {
  fullwidth: true,
  sized: "small",
};

const getBeneficiaryNotReadyReasonText = (reason: string) => {
  const beneficiaryReason = NOT_READY_TRANSACTION_OPTIONS_BENEFICIARY_MAPPING[reason];
  return beneficiaryReason || reason;
};

const getReasonFromAction = (
  transaction: TransactionWrap,
  direction: Direction,
  action: TransactionActionParsed,
  businessDaysSinceRequestForInfo?: number,
  hasRequestTrackingForMissingDataSetting?: boolean,
): string => {
  const { status } = transaction;

  if (status === TransactionStatus.INCOMPLETE && hasRequestTrackingForMissingDataSetting) {
    return getRequestMessage(businessDaysSinceRequestForInfo).message;
  }

  if (
    [
      TransactionStatus.SAVED,
      TransactionStatus.WAITING_FOR_INFORMATION,
      TransactionStatus.MISSING_BENEFICIARY_DATA,
    ].includes(status)
  )
    return "";

  if (!action) {
    return "";
  }

  const { action: actionName, actionData } = action;
  const callSource = actionData?.callSource ? actionData.callSource : undefined;
  const isOutgoing = direction === "outgoing";

  if (status === "NOT_READY") {
    const reasonText = isOutgoing ? getBeneficiaryNotReadyReasonText(actionData?.reason ?? "") : actionData?.reason;
    const reasonPrefix = isOutgoing ? "Beneficiary VASP is" : "You are";
    const reasonTooltipText = `${reasonPrefix} not ready to respond to this data transfer. ${
      reasonText ? `Reason: ${reasonText}` : ""
    }`;
    return reasonTooltipText;
  }

  if (isOutgoing) {
    // Actions made by the beneficiary
    if (status === "ACK" || status === "ACCEPTED" || status === "DECLINED" || status === "REJECTED")
      return "by beneficiary";

    // Actions made by the originator
    if (status === "CANCELLED" || status === "SENT") {
      if (callSource === "dashboard") {
        return "by manual review";
      } else if (callSource === "rules") {
        return "by your rules";
      } else if (callSource === "api") {
        return "via API";
      }
      return "by manual review";
    }

    if (status === "NEW") {
      if (actionName === "send_to_inbox") {
        return "Review triggered by rules";
      }
      return "";
    }
  } else {
    // Actions made by the beneficiary
    if (status === "ACCEPTED" || status === "DECLINED" || status === "REJECTED") {
      if (callSource === "dashboard") {
        return "by manual review";
      } else if (callSource === "rules") {
        return "by your rules";
      } else if (callSource === "api") {
        return "via API";
      }
      return "by manual review";
    }
    if (status === "ACK") {
      if (actionName === "confirm" && callSource === "auto") {
        return "Auto-identified";
      } else {
        return "Identified manually";
      }
    }

    // Actions made by the originator
    if (status === "CANCELLED") return "by originator";
    if (status === "SENT") {
      if (actionName === "send_to_inbox" && callSource === "rules") {
        return "Review triggered by rules";
      }
      return "";
    }
  }
  return "";
};

const TransactionRowCellStatus = ({
  transaction,
  hasRequestTrackingForMissingDataSetting,
  ...props
}: {
  transaction: TransactionWrap;
  hasRequestTrackingForMissingDataSetting: boolean | undefined;
} & React.ComponentProps<"div">) => {
  const vaspDID = useCurrentViewerVaspDID();

  const direction = transaction?.direction({ did: vaspDID })[2] || "incoming";

  if (transaction.actions && transaction.actions.length > 0) {
    transaction.actions.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }

  // There's always at least one action
  const actionsParsed = _.map(transaction?.actions, (action) => {
    return { ...action, actionData: (action.actionData ? JSON.parse(action.actionData) : null) as ActionData | null };
  });
  const actionParsed = _.find(actionsParsed, (action) => action.actionData?.vaspDID === vaspDID) ?? ({} as any);

  const color =
    transaction.businessDaysSinceRequestForInfo &&
    transaction.businessDaysSinceRequestForInfo >= 5 &&
    transaction.status === "INCOMPLETE"
      ? "danger"
      : "dark";

  const { type, value } = getProgress({
    status: transaction.status,
    businessDaysSinceRequestForInfo: transaction.businessDaysSinceRequestForInfo,
    hasRequestTrackingForMissingDataSetting,
  });

  const renderProgressBar = (
    progressBarType: "dark" | "success" | "warning" | "danger",
    progressBarValue: string,
    status: TransactionStatus,
    businessDaysSinceRequestForInfo?: number,
  ) => {
    if (
      status === TransactionStatus.INCOMPLETE &&
      businessDaysSinceRequestForInfo === undefined &&
      hasRequestTrackingForMissingDataSetting
    ) {
      return null;
    }

    return <ProgressBarStatus kind={progressBarType} value={progressBarValue} />;
  };

  const renderDescriptionStatus = () => {
    const content =
      transaction.status !== "NOT_READY"
        ? getReasonFromAction(
            transaction,
            direction,
            actionParsed,
            transaction.businessDaysSinceRequestForInfo,
            hasRequestTrackingForMissingDataSetting,
          )
        : "";

    if (transaction.status === "INCOMPLETE" && transaction.businessDaysSinceRequestForInfo !== undefined) {
      const missingDataMessage = "Missing data requested";
      const requestDate = formatterDateTime.format(transaction.updatedAt);

      const deadlineMessage = "The transfer may be declined within 3-5 days if you don't provide the missing data.";

      return (
        <>
          <DescriptionStatus data-tip data-for={transaction.id}>
            {content}
          </DescriptionStatus>
          <ReactTooltip id={transaction.id} backgroundColor="black">
            <TooltipContentContainer>
              <TooltipText>{missingDataMessage}</TooltipText>
              <TooltipDateText color="#f45b68">{requestDate}</TooltipDateText>

              {direction === "outgoing" && (
                <>
                  <Spacer axis={Axis.VERTICAL} size={16} />
                  <TooltipText>{deadlineMessage}</TooltipText>
                </>
              )}
            </TooltipContentContainer>
          </ReactTooltip>
        </>
      );
    }

    return <DescriptionStatus>{content}</DescriptionStatus>;
  };

  return (
    <div {...props} data-tip data-for={transaction.id}>
      <TextStatus color={color}>{getStatusText(transaction, direction)}</TextStatus>
      {renderDescriptionStatus()}
      {renderProgressBar(type, value, transaction.status, transaction.businessDaysSinceRequestForInfo)}
      {transaction.status === "NOT_READY" && (
        <ReactTooltip id={transaction.id} backgroundColor="black">
          <div style={{ width: "225px" }}>
            <div>{getReasonFromAction(transaction, direction, actionParsed)}</div>
          </div>
        </ReactTooltip>
      )}
    </div>
  );
};

export default TransactionRowCellStatus;
